import { usePageTitle } from "@/lib/usePageTitle";
import { Link } from "react-router-dom";
import { Phone, Clock, ArrowRight, CheckCircle2, Quote } from "lucide-react";
import { COMPANY } from "@/data/content";
import { useLocale } from "@/i18n/LocaleContext";
import FadeIn from "@/components/site/FadeIn";
import Icon from "@/components/site/Icon";
import GoogleReviews from "@/components/site/GoogleReviews";
import { PrimaryLink, CoralLink, Eyebrow } from "@/components/site/ui";

const HERO_IMG = "/images/hero-flyer.jpg";

export default function Home() {
  usePageTitle("home");
  const { t, path } = useLocale();
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-brand-gradient text-white">
        <div className="absolute inset-0 bg-brand-radial" aria-hidden="true" />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)",
            backgroundSize: "26px 26px",
          }}
          aria-hidden="true"
        />
        <div className="relative max-w-7xl mx-auto px-5 md:px-10 pt-14 pb-16 md:pt-20 md:pb-24">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <FadeIn>
              <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-white/5 px-4 py-1.5 text-sm font-600 uppercase tracking-[0.12em] text-gold">
                {t.home.eyebrow}
              </span>
              <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-600 leading-[1.1] text-white">
                {t.home.h1a}{" "}
                <span className="italic text-gold">{t.home.h1b}</span>
              </h1>
              <p className="mt-6 text-lg md:text-xl leading-relaxed text-white/80 max-w-xl">
                {t.home.lead}
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <CoralLink href={`tel:${COMPANY.phoneRaw}`} data-testid="hero-call-cta">
                  <Phone className="w-5 h-5 shrink-0" />
                  <span className="text-center leading-tight">
                    {t.common.callNow}{" "}
                    <span className="whitespace-nowrap">{COMPANY.phone}</span>
                  </span>
                </CoralLink>
                <Link
                  to={path("contact")}
                  data-testid="hero-contact-cta"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white/30 bg-white/5 px-7 py-4 text-lg font-600 text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/10 hover:border-white/50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/30"
                >
                  {t.common.contactUs} <ArrowRight className="w-5 h-5 rtl:rotate-180" />
                </Link>
              </div>

              <div
                data-testid="hero-trust-badge"
                className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/70"
              >
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gold" /> {t.common.aroundTheClock}{" "}
                  <a href={`tel:${COMPANY.emergencyRaw}`} className="font-600 text-white hover:text-gold">
                    {COMPANY.emergency}
                  </a>
                </span>
                <span className="flex items-center gap-2">
                  <Icon name="BadgeCheck" className="w-4 h-4 text-gold" /> {t.common.certifiedStaff}
                </span>
              </div>
            </FadeIn>

            <FadeIn delay={0.15}>
              <figure className="relative">
                <img
                  src={HERO_IMG}
                  alt={t.home.heroImgAlt}
                  width={1145}
                  height={1374}
                  loading="eager"
                  className="w-full h-auto rounded-2xl bg-white shadow-[0_30px_80px_rgba(0,0,0,0.45)] ring-1 ring-white/10"
                />
                <figcaption className="mt-2 text-end text-xs text-white/60">{t.common.symbolImage}</figcaption>
              </figure>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* WILLKOMMEN */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-5 md:px-10 py-16 md:py-24">
          <FadeIn className="text-center">
            <Quote className="w-10 h-10 text-coral mx-auto" aria-hidden="true" />
            <p className="mt-6 font-serif text-2xl md:text-3xl leading-relaxed text-brand-dark">
              „{t.home.welcomeQuote}“
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 3 FEATURE-CARDS */}
      <section className="bg-cream">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {t.home.features.map((f, i) => (
              <FadeIn key={f.title} delay={i * 0.1}>
                <Link
                  to={path(f.to)}
                  data-testid={`feature-card-${i}`}
                  className="group flex flex-col h-full rounded-2xl bg-white p-8 border border-border shadow-[0_8px_30px_rgb(44,51,51,0.06)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgb(44,51,51,0.12)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/30"
                >
                  <span className="flex items-center justify-center w-14 h-14 rounded-2xl bg-brand-light text-brand">
                    <Icon name={f.icon} className="w-7 h-7" />
                  </span>
                  <h3 className="mt-5 text-2xl font-600 text-brand-dark">
                    {f.title}
                  </h3>
                  <p className="mt-3 text-base text-muted-foreground leading-relaxed flex-1">
                    {f.text}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-2 font-600 text-coral-hover group-hover:gap-3 transition-all">
                    {f.cta} <ArrowRight className="w-5 h-5 rtl:rotate-180" />
                  </span>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* LEISTUNGSÜBERSICHT */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
          <FadeIn className="max-w-2xl">
            <Eyebrow>{t.home.servicesEyebrow}</Eyebrow>
            <h2 className="mt-4 text-3xl md:text-4xl font-600 text-brand-dark leading-snug">
              {t.home.servicesTitle}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">{t.home.servicesLead}</p>
          </FadeIn>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {t.services.map((s, i) => (
              <FadeIn key={s.id} delay={(i % 3) * 0.1}>
                <Link
                  to={path("services", s.id)}
                  data-testid={`service-tile-${s.id}`}
                  className="group flex flex-col h-full rounded-2xl bg-cream p-8 border border-border transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-[0_12px_40px_rgb(44,51,51,0.10)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/30"
                >
                  <span className="flex items-center justify-center w-14 h-14 rounded-2xl bg-white text-brand shadow-sm group-hover:bg-brand group-hover:text-white transition-colors">
                    <Icon name={s.icon} className="w-7 h-7" />
                  </span>
                  <h3 className="mt-5 text-xl font-600 text-brand-dark">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-base text-muted-foreground leading-relaxed">
                    {s.short}
                  </p>
                </Link>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* WERTE / VERTRAUEN */}
      <section className="bg-brand-dark text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeIn>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm font-600 uppercase tracking-wide text-gold">
                {t.home.valuesEyebrow}
              </span>
              <h2 className="mt-4 text-3xl md:text-4xl font-600 leading-snug">{t.home.valuesTitle}</h2>
              <p className="mt-4 text-lg text-white/80 leading-relaxed">{t.home.valuesLead}</p>
            </FadeIn>
            <FadeIn delay={0.15}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {t.values.map((v) => (
                  <div
                    key={v.label}
                    className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-5"
                  >
                    <span className="flex items-center justify-center w-11 h-11 rounded-full bg-gold/15 text-gold shrink-0">
                      <Icon name={v.icon} className="w-6 h-6" />
                    </span>
                    <span className="font-600 text-base text-white min-w-0 break-words hyphens-auto">{v.label}</span>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* GOOGLE-BEWERTUNGEN */}
      <GoogleReviews />

      {/* CTA-BANNER */}
      <section className="bg-cream">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-24">
          <FadeIn>
            <div className="rounded-2xl bg-coral/15 border border-coral/30 p-8 md:p-12 text-center">
              <h2 className="text-3xl md:text-4xl font-600 text-brand-dark leading-snug">{t.home.ctaTitle}</h2>
              <p className="mt-4 text-lg text-ink max-w-2xl mx-auto">{t.home.ctaLead}</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center items-center">
                <PrimaryLink to={path("contact")} data-testid="cta-banner-appointment">
                  {t.common.bookAppointment} <ArrowRight className="w-5 h-5 rtl:rotate-180" />
                </PrimaryLink>
                <a
                  href={`tel:${COMPANY.phoneRaw}`}
                  className="inline-flex items-center gap-2 text-lg font-600 text-brand-dark hover:text-brand"
                >
                  <Phone className="w-5 h-5 text-coral-hover" /> {COMPANY.phone}
                </a>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
